import { useQuery, useMutation, useQueryClient, useQueries } from "@tanstack/react-query";
import { albumService } from "../services/albumService";
import { AlbumShare, AlbumWithShare } from "../types/album";
import { handleApiError, getErrorMessage } from "../utils/errorHandler";
import Session from "supertokens-auth-react/recipe/session";
import { albumKeys } from "./useAlbumsQuery";

export const sharedAlbumKeys = {
  all: ['sharedAlbums'] as const,
  list: (userId: string) => [...sharedAlbumKeys.all, userId] as const,
  details: (userId: string) => [...sharedAlbumKeys.all, userId, 'details'] as const,
};

export const useSharedAlbums = (userId?: string) => {
  const queryClient = useQueryClient();

  const {
    data: sharedAlbums = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: sharedAlbumKeys.list(userId!),
    queryFn: async () => {
      const sessionExists = userId ? await Session.doesSessionExist() : false;
      if (!userId || !sessionExists) {
        throw new Error("User not authenticated");
      }
      return albumService.getSharedAlbums(userId);
    },
    enabled: !!userId,
  });

  // Shares on albums you own (e.g. you opened your own invite links) also appear in "My Albums".
  // Exclude them here so "Shared with Me" / merged "All" does not list the same album twice.
  const sharesFromOthers = userId
    ? sharedAlbums.filter((share) => share.ownerId !== userId)
    : [];

  // Deduplicate albums: if user has both read_only and upload for same album, keep only upload
  const deduplicatedShares = sharesFromOthers.reduce<AlbumShare[]>((acc, share) => {
    const existingIndex = acc.findIndex(
      (s) => s.albumId === share.albumId && s.ownerId === share.ownerId
    );

    if (existingIndex === -1) {
      // No duplicate found, add this share
      acc.push(share);
    } else {
      // Duplicate found, keep the one with upload permission
      if (share.permission === "upload") {
        acc[existingIndex] = share;
      }
      // If current share is read_only, keep the existing one (which might be upload)
    }

    return acc;
  }, []);

  // Fetch album details for each share using React Query
  const albumQueries = useQueries({
    queries: deduplicatedShares.map((share) => ({
      queryKey: albumKeys.detail(share.ownerId, share.albumId),
      queryFn: async () => {
        const sessionExists = await Session.doesSessionExist();
        if (!sessionExists) {
          throw new Error("User not authenticated");
        }
        return albumService.getAlbum(share.ownerId, share.albumId);
      },
      enabled: !!share.ownerId && !!share.albumId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  });

  // Combine album details with share metadata
  const sharedAlbumsWithDetails: AlbumWithShare[] = deduplicatedShares
    .map((share, index) => {
      const query = albumQueries[index];
      if (!query.data) return null;

      return {
        ...query.data,
        isShared: true,
        sharePermission: share.permission,
      } as AlbumWithShare;
    })
    .filter((album): album is AlbumWithShare => album !== null);

  const isLoadingDetails = albumQueries.some((q) => q.isLoading);
  const hasErrors = albumQueries.some((q) => q.error);

  const deleteShareMutation = useMutation({
    mutationFn: async (shareId: string) => {
      const sessionExists = userId ? await Session.doesSessionExist() : false;
      if (!userId || !sessionExists) {
        throw new Error("User not authenticated");
      }
      return albumService.deleteAlbumShare(userId, shareId);
    },
    onSuccess: (_, shareId) => {
      queryClient.setQueryData<AlbumShare[]>(sharedAlbumKeys.list(userId!), (old) => {
        return old ? old.filter((share) => share.id !== shareId) : [];
      });
    },
    onError: (error) => {
      console.error("Error deleting share:", handleApiError(error));
    },
  });

  return {
    // Data
    sharedAlbums: deduplicatedShares,
    sharedAlbumsWithDetails,
    hasSharedAlbums: deduplicatedShares.length > 0,

    // Loading states
    isLoading: isLoading || isLoadingDetails,
    isDeleting: deleteShareMutation.isPending,

    // Error
    error: error ? getErrorMessage(handleApiError(error)) : hasErrors ? "Failed to load some shared albums" : null,

    // Actions
    deleteShare: deleteShareMutation.mutateAsync,
    refreshSharedAlbums: refetch,

    // Query client for manual invalidation
    invalidateSharedAlbums: () => queryClient.invalidateQueries({ queryKey: sharedAlbumKeys.list(userId!) }),
  };
};
